import Image from "next/image";
import { AI_TOOL_OPTIONS, type AiToolId } from "@cw/forms/ai-tools";
import { FieldError, HINT_CLASS, LABEL_CLASS } from "./fields";
import { AI_TOOL_LOGOS, AI_TOOL_MONOGRAMS } from "./intake-options";

type AiToolTilesProps = {
  value: AiToolId[];
  onToggle: (id: AiToolId) => void;
  error?: string;
};

function ToolMark({ id, label }: { id: AiToolId; label: string }) {
  const logo = AI_TOOL_LOGOS[id];
  if (logo) return <Image src={logo} alt="" width={24} height={24} className="size-6 shrink-0 object-contain" />;
  return (
    <span aria-hidden="true" className="inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-accent-soft text-[11px] font-semibold text-ink">
      {AI_TOOL_MONOGRAMS[id] ?? label.slice(0, 1)}
    </span>
  );
}

/** Multi-select integration tiles. Checkbox semantics stay native; the tile is the visible control. */
export function AiToolTiles({ value, onToggle, error }: AiToolTilesProps) {
  return (
    <fieldset aria-describedby={error ? "edge-ai-tools-error" : "edge-ai-tools-hint"}>
      <legend className={LABEL_CLASS}>Which AI tools does your team use today?</legend>
      <p id="edge-ai-tools-hint" className={`${HINT_CLASS} mt-1`}>Optional. Select all that apply so we can plan how Edge connects to them.</p>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {AI_TOOL_OPTIONS.map((option) => {
          const isChecked = value.includes(option.value);
          return (
            <label key={option.value} className="relative block cursor-pointer">
              <input className="peer sr-only" type="checkbox" name="aiTools" value={option.value} checked={isChecked} onChange={() => onToggle(option.value)} />
              <span className="flex min-h-12 items-center gap-3 rounded-lg border border-hairline bg-paper-raised py-2.5 pl-3 pr-9 text-sm font-medium text-ink transition-[border-color,background-color] duration-150 hover:border-slate/40 peer-checked:border-ink peer-checked:bg-accent-tint peer-focus-visible:outline-2 peer-focus-visible:outline-offset-3 peer-focus-visible:outline-ink">
                <ToolMark id={option.value} label={option.label} />
                <span className="leading-tight">{option.label}</span>
              </span>
              <span aria-hidden="true" className="absolute right-3 top-1/2 hidden size-4 -translate-y-1/2 items-center justify-center rounded-full bg-ink text-paper-raised peer-checked:inline-flex">
                <svg viewBox="0 0 16 16" className="size-2.5" fill="none" stroke="currentColor" strokeWidth="2.4"><path strokeLinecap="round" strokeLinejoin="round" d="m3 8.5 3 3 7-7" /></svg>
              </span>
            </label>
          );
        })}
      </div>
      <FieldError id="edge-ai-tools-error" message={error} />
    </fieldset>
  );
}
